import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import {
  createEmployee,
  deleteEmployee,
  fetchEmployees,
  importEmployees,
  updateEmployee,
  type EmployeeWriteBody,
} from './api'

export const EMPLOYEES_KEY = ['employees'] as const

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"). CSV import is
// excluded — its File payload can't serialize into the persisted queue.
export const EMPLOYEE_CREATE_KEY = ['outbox', 'employees', 'create'] as const
export const EMPLOYEE_UPDATE_KEY = ['outbox', 'employees', 'update'] as const
export const EMPLOYEE_DELETE_KEY = ['outbox', 'employees', 'delete'] as const

queryClient.setMutationDefaults(EMPLOYEE_CREATE_KEY, { mutationFn: createEmployee })
queryClient.setMutationDefaults(EMPLOYEE_UPDATE_KEY, {
  mutationFn: ({ id, body }: { id: string; body: EmployeeWriteBody }) => updateEmployee(id, body),
})
queryClient.setMutationDefaults(EMPLOYEE_DELETE_KEY, { mutationFn: deleteEmployee })

export function useEmployees() {
  return useQuery({
    queryKey: EMPLOYEES_KEY,
    queryFn: fetchEmployees,
  })
}

export function useCreateEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: EMPLOYEE_CREATE_KEY,
    mutationFn: (body: EmployeeWriteBody) => createEmployee(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: EMPLOYEES_KEY }),
  })
}

export function useUpdateEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: EMPLOYEE_UPDATE_KEY,
    mutationFn: ({ id, body }: { id: string; body: EmployeeWriteBody }) => updateEmployee(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: EMPLOYEES_KEY }),
  })
}

export function useDeleteEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: EMPLOYEE_DELETE_KEY,
    mutationFn: (id: string) => deleteEmployee(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: EMPLOYEES_KEY }),
  })
}

export function useImportEmployees() {
  const qc = useQueryClient()
  return useMutation({
    // File payload can't serialize into the persisted outbox — fail fast
    // offline instead of pausing.
    networkMode: 'always',
    mutationFn: (file: File) => importEmployees(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: EMPLOYEES_KEY }),
  })
}
